<script lang="ts">
  import { faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons';
  import type { PanelSections } from '../panel-sections.svelte';
  import Icon from './Icon.svelte';
  import type { PanelSection } from './panel-section';

  /** `side` is where the panel sits beside the canvas; `name` is how buttons call it. */
  const {
    panel,
    sections,
    side,
    name,
    controls,
    locked,
  }: {
    panel: PanelSections;
    sections: readonly PanelSection[];
    side: 'left' | 'right';
    name: string;
    controls: string;
    locked: boolean;
  } = $props();

  // The arrow points the way the column will move.
  const toward = $derived((side === 'left') === panel.collapsed ? faAnglesRight : faAnglesLeft);
</script>

<div class="rail {side}" class:collapsed={panel.collapsed} role="group" aria-label="Sections">
  {#each sections as section (section.id)}
    <button
      type="button"
      class="rail-icon"
      aria-label={section.label}
      aria-pressed={panel.isShown(section.id)}
      title={panel.isShown(section.id) ? `Hide ${name}` : `Show ${section.label}`}
      disabled={locked && (panel.collapsed || panel.isShown(section.id))}
      onclick={() => panel.show(section.id)}
    >
      <Icon icon={section.icon} />
    </button>
  {/each}
  <span class="spacer"></span>
  <button
    type="button"
    class="rail-icon"
    aria-label={panel.collapsed ? `Show ${name}` : `Hide ${name}`}
    aria-expanded={!panel.collapsed}
    aria-controls={controls}
    title={panel.collapsed ? `Show ${name}` : `Hide ${name}`}
    disabled={locked}
    onclick={() => panel.setCollapsed(!panel.collapsed)}
  >
    <Icon icon={toward} />
  </button>
</div>

<style>
  .rail {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    width: 2.5rem;
    padding: var(--space-2) 0;
  }

  .rail.left:not(.collapsed) {
    border-right: 1px solid var(--color-border);
  }

  .rail.right:not(.collapsed) {
    border-left: 1px solid var(--color-border);
  }

  .spacer {
    flex: 1;
  }

  .rail-icon {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 1px solid transparent;
    border-radius: var(--radius);
    background: none;
    color: var(--color-text-muted);
    font-size: 0.875rem;
    cursor: pointer;
  }

  .rail-icon:hover {
    border-color: var(--color-border);
  }

  /* Lighter and darker as well as tinted, so it reads without color vision. */
  .rail-icon[aria-pressed='true'] {
    background: color-mix(in srgb, var(--color-accent) 18%, transparent);
    border-color: var(--color-accent);
    color: var(--color-text);
  }

  .rail-icon:disabled {
    opacity: 0.45;
    cursor: default;
  }
</style>
